import React from 'react';
import { NodeProps, useEdges, useNodes, useReactFlow } from 'reactflow';
import { Select } from '../../../form/select';
import { Textarea } from '../../../form/textarea';
import { operationType, selectDataType } from '../../../../services/types';
import { Main, Styled } from './style';
import { SettingsBlock, settingsDataType } from '../../../settingsBlock';
import { IoCheckmarkCircleOutline, IoWarningOutline } from 'react-icons/io5';
import { ExpandedBlock } from '../../expanded';


type LlamaProps = NodeProps<operationType> & {
   setChildMinimize?: (fn: (id: string) => void) => void;
   setChildSettings?: (fn: (id: string) => void) => void;
};

export function Llama({ id, data, setChildMinimize, setChildSettings }: LlamaProps) {
   const [models, setModels] = React.useState<selectDataType[]>([
      { id: 1, name: 'llama3.1-405b', refId: 'gemini-2.5-flash', isActive: true },
      { id: 2, name: 'llama3.3-70b', refId: 'gemini-2.0-flash', isActive: false }
   ]);
   const { setNodes } = useReactFlow();
   const nodes = useNodes();
   const edges = useEdges();
   const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
   const [isExpanded, setIsExpanded] = React.useState(false);
   
   // --
   React.useEffect(() => {
      if (setChildMinimize) setChildMinimize(() => { setIsExpanded(prev => !prev) });
      if (setChildSettings) setChildSettings(() => { setSettings(prev => ({ ...prev, isToggle: !prev.isToggle })) });
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [setChildMinimize, setChildSettings]);

   // ---
   React.useEffect(() => {
      if (!data.value) {
         setNodes((nds) => nds.map((node) => {
            if (node.id === id) return { ...node, data: { ...node.data, value: { model: models[0].refId, instructions: 'You are a helpful Assistant.' }}};
            else return node;
         }));
      } else {
         setModels(models => models.map(model => ({ ...model, isActive: model.refId === data.value.model })));
      }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   // ---
   const handleSelectModel = (selectId: number) => {
      setModels((models) => models.map((model) => {
         if (model.id === selectId) return { ...model, isActive: true };
         else return { ...model, isActive: false };
      }));
      setNodes((nds) => nds.map((node) => {
         if (node.id === id) return { ...node, data: { ...node.data, value: { ...node.data.value, model: models.find(m => m.id === selectId)?.refId }}};
         else return node;
      }));
   }
   // ---
   const handleChangeInstructions = React.useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setNodes((nds) => nds.map((node) => {
         if (node.id === id) return { ...node, data: { ...node.data, value: { ...node.data.value, instructions: e.target.value }}};
         else return node;
      }));
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);
   
   // @ts-ignore
   const targetInput = edges.filter(edge => edge.target === id && nodes.some(node => node.id === edge.source && node.data?.action === 'input'));
   const relationsInput = nodes.filter(node => targetInput.some(edge => edge.source === node.id));
   // @ts-ignore
   const targetExternal = edges.filter(edge => edge.target === id && nodes.some(node => node.id === edge.source && (node.data?.action === 'uploadImages' || node.data?.outputType === 'text')));
   const relationsExternal = nodes.filter(node => targetExternal.some(edge => edge.source === node.id));
   // ---
   const isWarning = !relationsInput.length && !relationsExternal.length;
   
   return (<>
      <SettingsBlock nodeId={ id }data={ settings } setData={ setSettings } />
      <Main>
         {isWarning ? <>
            <div id="warning"></div>
            <span id="warningIcon"><IoWarningOutline size={21} /></span>
         </> : data.output ? <>
            <div id="ran"></div>
            <span id="ranIcon"><IoCheckmarkCircleOutline size={21} /></span>
         </> : null}
         { isExpanded ? <ExpandedBlock
            data={{ id, ...data }} 
            handleClose={ () => setIsExpanded(false) } 
            isWarning={ isWarning }
            style={{ minWidth: '380px' }}
         ><Styled>
            <Select title="Model" data={ models } handleSelectItem={ handleSelectModel } />
            <Textarea title="Instructions" value={ data.value?.instructions } handleChange={ handleChangeInstructions } />
            <div className="title">Inputs</div>
            <div className="inputs nodrag nowheel">
               <div className="item">
                  <div>
                     <span className={ relationsInput.length ? 'green' : 'red' }></span>
                     <span>User Input</span> 
                  </div>
                  <ul>{ // @ts-ignore
                     relationsInput.length ? relationsInput.map(node => (<li key={ node.id }>{ node.data?.index }</li>)) : <li>N/A</li>
                  }</ul>
               </div>
               <div className="item">
                  <div>
                     <span className={ relationsExternal.length ? 'green' : 'red' }></span>
                     <span>External Knowledge</span>
                  </div>
                  <ul>{ // @ts-ignore
                     relationsExternal.length ? relationsExternal.map(node => (<li key={ node.id }>{ node.data?.index }</li>)) : <li>N/A</li>
                  }</ul>
               </div>
            </div>
         </Styled></ExpandedBlock> : null}
      </Main>
   </>);
}